import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {
    addCurrency,
    BaseMenuPageComponent,
    CompDefinition,
    CompDefinitionAvailabilities,
    CompDefinitionTypes,
    CreateOrderItemCreditComponent,
    CustomerAreaPosition,
    CustomerAreaPositionService,
    DeviceService,
    divideCurrency,
    LibConfig,
    LibConfigService,
    LoadingService,
    Membership,
    MembershipProgramFeeStructure,
    MembershipRenewal,
    MembershipService,
    MenuService,
    multiplyCurrency,
    OfflineOrderService,
    OfflineTabService,
    OrderItem,
    OrderItemCredit,
    OrderItemService,
    OrderNavigation,
    OrderService,
    OrganizationService,
    OrganizationTerminalService,
    Person,
    PersonService,
    PickupLocation,
    PickupLocationMenuService,
    PickupLocationService,
    PrinterService,
    subtractCurrency,
    Tab,
    TabCalculationService,
    TabCredit,
    TableMarkerEntryComponent,
    TabNoteDefinition,
    TabService,
    TabStatuses,
    ToastService,
    UnderscoreConversionPipe,
    User,
    UserService
} from 'brewbill-lib';

import {ActivatedRoute, Router} from '@angular/router';
import {AlertController, ModalController, NavController} from '@ionic/angular';
import {SelectCompComponent} from '../select-comp/select-comp.component';
import {clone} from 'chart.js/helpers';
import {SelectMemberComponent} from '../select-member/select-member.component';
import {RenewMembershipComponent} from '../renew-membership/renew-membership.component';
import {EditMembershipComponent} from '../edit-membership/edit-membership.component';

@Component({
    selector: 'bb-manual-order',
    templateUrl: './manual-order.component.html',
    styleUrls: ['./manual-order.component.scss'],
})
export class ManualOrderComponent extends BaseMenuPageComponent implements OnInit {
    isTerminal = true;
    availableTabComps: CompDefinition[] = [];
    availableOrderComps: CompDefinition[] = [];
    openTabs: Tab[];
    currentUser: User;
    deviceId: string;
    active = false;
    customerAreaPosition: CustomerAreaPosition;

    testMode = false;

    constructor(
        @Inject(LibConfigService) private config: LibConfig,
        private modalController: ModalController,
        private offlineOrderService: OfflineOrderService,
        private deviceService: DeviceService,
        private printerService: PrinterService,
        private personService: PersonService,
        private customerAreaPositionService: CustomerAreaPositionService,
        private underscoreConversionPipe: UnderscoreConversionPipe,
        membershipService: MembershipService,
        pickupLocationService: PickupLocationService,
        toastService: ToastService,
        changeDetectorRef: ChangeDetectorRef,
        pickupLocationMenuService: PickupLocationMenuService,
        orderService: OrderService,
        orderItemService: OrderItemService,
        tabCalculationService: TabCalculationService,
        loadingService: LoadingService,
        menuService: MenuService,
        tabService: TabService,
        organizationTerminalService: OrganizationTerminalService,
        userService: UserService,
        organizationService: OrganizationService,
        route: ActivatedRoute,
        alertController: AlertController,
        router: Router,
        navController: NavController,
        offlineTabService: OfflineTabService
    ) {
        super(membershipService,
            pickupLocationService,
            toastService,
            changeDetectorRef,
            pickupLocationMenuService,
            orderService,
            tabCalculationService,
            orderItemService,
            loadingService,
            menuService,
            tabService,
            organizationTerminalService,
            userService,
            organizationService,
            route,
            alertController,
            router,
            navController,
            offlineTabService);
    }

    ngOnInit() {
        super.ngOnInit();

        this.customerAreaPosition = !!this.customerAreaPositionService.currentValue
            ? new CustomerAreaPosition(this.customerAreaPositionService.currentValue)
            : null;

        this.subscribe(this.membershipService.current.subscribe(m => {
            if (!!m && m.renewing) {
                this.renewMembership(null);
            } else {
                this.activeMembership = !!m ? new Membership(m) : null;
                if (!!this.tab) {
                    this.tab.member = this.activeMembership;
                }
                this.applyAll(this.order);
            }
        }));

        this.subscribe(this.userService.current.subscribe(u => {
            this.currentUser = !!u ? new User(u) : null;
            this.checkAvailableComps();
        }));

        this.subscribe(this.tabService.openTabs.subscribe(ot => {
            this.openTabs = ot;
        }));

        this.subscribe(this.customerAreaPositionService.current.subscribe(c => this.customerAreaPosition = !!c
            ? new CustomerAreaPosition(c)
            : null));

        this.subscribe(this.tabService.deletedTab.subscribe(id => {
            if (!!id && !!this.tab && this.tab.id === id) {
                this.toastService.message('This tab has been deleted.');
                this.tabService.setCurrent(null);
            }
        }));

        this.deviceService.getDeviceId().then(d => this.deviceId = d);
    }

    ionViewDidEnter() {
        this.active = true;
    }

    ionViewWillLeave() {
        this.active = false;
    }

    checkAvailableComps() {
        if (!!this.currentUser && !!this.organization && !!this.organization.compDefinitions) {
            const tabComps = this.organization.compDefinitions.filter(c => c.availability === CompDefinitionAvailabilities.TAB);
            const orderComps = this.organization.compDefinitions.filter(c => c.availability === CompDefinitionAvailabilities.ORDER_ITEM);

            if (this.currentUser.siteAdmin) {
                this.availableTabComps = tabComps;
                this.availableOrderComps = orderComps;
            } else {
                const organizationUser = this.currentUser.organizations.find(o => o.orgId === this.organization.id);
                if (organizationUser.admin || organizationUser.owner) {
                    this.availableTabComps = tabComps;
                    this.availableOrderComps = orderComps;
                } else {
                    this.availableTabComps = tabComps
                        .filter(c => !c.roles
                            || c.roles.length === 0
                            || c.roles.some(r => organizationUser.roles.some(orgRole => orgRole.id === r.id)));

                    this.availableOrderComps = orderComps
                        .filter(c => !c.roles
                            || c.roles.length === 0
                            || c.roles.some(r => organizationUser.roles.some(orgRole => orgRole.id === r.id)));
                }
            }
        } else {
            this.availableTabComps = [];
            this.availableOrderComps = [];
        }
    }

    organizationChanged() {
        this.checkAvailableComps();
    }

    pickupLocationChanged(p: PickupLocation) {
        this.pickupLocation = !!p ? new PickupLocation(p) : null;
        if (!!this.order) {
            this.applyAll(this.order);
        }
    }

    async tabChanged(t: Tab) {
        const tab = !!t ? new Tab(t) : null;

        if (this.active
            && !!tab
            && !!this.tab
            && this.tab.id === tab.id
            && tab.status === TabStatuses.CLOSED
            && this.tab.status === TabStatuses.OPEN) {
            await this.toastService.message('This tab has been closed.');
            await this.navController.navigateBack(`/manager/${this.pickupLocation.id}/tab/${this.tab.id}`);
            this.tab = null;
        } else {
            if (!!tab && !tab.id) {
                const charge = this.tabCalculationService.adjustForPaymentMethod(tab, 'CHARGE', null, null);
                const cash = this.tabCalculationService.adjustForPaymentMethod(tab, 'CASH', null, null);
                if (!!charge && !!cash && (cash.due !== charge.due || !!charge.surcharge)) {
                    tab.chargeDue = addCurrency(charge.due, charge.surcharge);
                    tab.cashDue = cash.due;
                }
            }

            this.tab = tab;
            if (!!this.tab) {
                if (!!this.tab.member) {
                    this.activeMembership = new Membership(this.tab.member);
                }
            }
        }
    }

    async activateOrder(silent = false) {
        this.order.terminalOrder = true;
        this.order.parentId = this.tab.id;

        if (this.organization.autoDeliver && !silent) {
            this.loadingService.present();
        }

        const tab = this.tab;
        if (!!tab && !!tab.person && !tab.person.id) {
            this.order.person = tab.person;
        }

        this.orderService.activateOrder(this.order).subscribe(async (t: Tab) => {
            const saved = new Tab(t);
            this.tabService.setEvent(saved);
            this.tabService.setCurrent(saved);

            this.printerService.printAllOrderInformation(this.order, saved);

            if (this.organization.autoDeliver) {
                this.loadingService.dismiss();
                if (!this.pickupLocation || this.pickupLocation.orderNavigation === OrderNavigation.TAB) {
                    await this.navController.navigateBack(`/manager/${this.pickupLocation.id}/tab/${saved.id}`);
                } else if (this.pickupLocation.orderNavigation === OrderNavigation.OPEN_TABS) {
                    this.tabService.setCurrent(null);
                    await this.navController.navigateBack(`/manager/${this.pickupLocation.id}/tabs`);
                } else {
                    this.tabService.setCurrent(null);
                    await this.navController.navigateBack(`/manager/${this.pickupLocation.id}/manual-order`);
                }
                this.orderService.setCurrent(null);
            }
        }, error => {
            console.log(error);
            this.tab.orders.splice(0, 1);
            this.tabService.addToCache(this.tab);
            const current = this.tabService.currentValue;
            if (!!current && current.id === tab.id) {
                this.tabService.setCurrent(tab);
            }
            this.toastService.error('Failed to save order');
        });
    }

    async editPersonInfo(paymentType: string = null) {
        if (!paymentType || paymentType === 'START_BLANK_TAB') {
            const validationRequirement = this.personService.requirePersonInformation(this.tab, this.order);
            if (!validationRequirement.valid) {
                const person = await this.personService.editPersonInformation(validationRequirement) as Person;
                if (!!person) {
                    this.tab.person = person;
                    await this.nextAction(paymentType);
                }
            } else {
                await this.nextAction(paymentType);
            }
        } else {
            await this.nextAction(paymentType);
        }
    }

    async navigate(paymentType: string = null) {
        if (paymentType === 'START_BLANK_TAB') {
            let person = !!this.tab ? this.tab.person : null;
            if (!person && !this.activeMembership) {
                const validationRequirement = this.personService.requirePersonInformation(this.tab, this.order);
                person = await this.personService.editPersonInformation(validationRequirement) as Person;
            }

            const blankTab = new Tab({
                orders: !!this.order ? [this.order] : null,
                person: person,
                member: this.activeMembership,
                credits: !!this.tab ? this.tab.credits : [],
                customerAreaPosition: this.customerAreaPosition,
                membershipProgram: !!this.activeMembership ? this.activeMembership.activeMembershipProgram : null
            });

            this.loadingService.present();
            this.tabService.createBlank(this.pickupLocation.id, blankTab).subscribe(async (t: Tab) => {
                const tab = new Tab(t);
                this.tabService.setCurrent(tab);
                this.tabService.setEvent(tab);
                this.customerAreaPositionService.setCurrent(null);
                this.tabService.setPartySize(null);
                this.loadingService.dismiss();
                const order = !!tab && !!tab.orders && !!this.order && !!this.order.key
                    ? tab.orders.find(o => o.key === this.order.key) : this.order;
                this.printerService.printAllOrderInformation(!!order ? order : this.order, tab);
                await this.navController.navigateForward(`/manager/${this.pickupLocation.id}/tab/${t.id}`);
            });
        } else if (!!this.order && this.order.items.length > 0) {
            try {
                const order = this.offlineOrderService.initialize(this.order, this.deviceId);
                order.terminalOrder = this.organizationTerminalService.isTerminal;
                order.tableStandMarker = this.order.tableStandMarker;

                const tab: Tab = !!this.tab ? this.tab : this.offlineTabService.initialize(order, this.applyNotes);

                if (!!this.tab) {
                    tab.preventAutoCredit = this.tab.preventAutoCredit;
                    tab.credits = this.tab.credits;
                    const index = tab.orders.findIndex(o => !o.id);
                    if (index > -1) {
                        tab.orders[index] = order;
                    }
                }

                this.tabService.setCurrent(tab);

                if (paymentType === 'START_TAB') {
                    if (!!this.activeMembership && !!this.activeMembership.cardOnFile) {
                        this.memberStartTab(tab);
                    } else {
                        await this.navController.navigateForward(`/start-tab/${this.pickupLocation.id}/${tab.key}`);
                    }
                } else {
                    await this.navController.navigateForward(`/confirm-tab/${this.pickupLocation.id}/${tab.key}`,
                        {queryParams: {paymentType}});
                }
            } catch (error) {
                console.log(error);
            }
        }
    }

    async memberStartTab(tab: Tab) {
        const alert = await this.alertController.create({
            cssClass: 'brewbill-alert',
            header: 'Card on File',
            message: `Would you like to use the card ending in ${this.activeMembership.cardOnFile.lastFour}?`,
            buttons: [
                {
                    text: 'No',
                    handler: async () => {
                        await this.navController.navigateForward(`/start-tab/${this.pickupLocation.id}/${tab.key}`);
                    }
                }, {
                    text: 'Yes',
                    handler: () => {
                        this.loadingService.present();
                        this.tabService.startForMember(tab, this.activeMembership.id).subscribe(t => {
                            this.loadingService.dismiss();
                            this.membershipService.setCurrent(null);
                            this.tabService.setCurrent(null);
                            this.orderService.setCurrent(null);
                            this.navController.navigateBack(`manager/${this.pickupLocation.id}/manual-order`);
                        }, error => {
                            this.loadingService.dismiss();
                            this.tabService.startTabErrorHandler(error);
                        });
                    }
                }
            ]
        });

        await alert.present();
    }

    async checkTableStand(paymentType: string = null) {
        if (!!this.order
            && !!this.order
            && !this.order.tableStandMarker
            && !!this.order.items
            && this.order.items.some(i => !!i.menuItem && i.menuItem.requestTableStandMarker)) {

            const modal = await this.modalController.create({
                component: TableMarkerEntryComponent,
                componentProps: {
                    amount: '',
                    min: 0,
                    label: 'Table Marker'
                },
                breakpoints: [0.0, 1],
                initialBreakpoint: 1,
                cssClass: 'table-marker-input-modal'
            });
            await modal.present();

            modal.onDidDismiss().then(async (returnData) => {
                if (!!returnData && !!returnData.data) {
                    this.order.tableStandMarker = (returnData.data.marker !== 'NONE') ? parseInt(returnData.data.marker, 10) : null;
                    this.order.notes = returnData.data.notes;
                    this.orderService.setCurrent(this.order);
                    await this.editPersonInfo(paymentType);
                }
            });
        } else {
            await this.editPersonInfo(paymentType);
        }
    }

    async nextAction(paymentType: string = null) {
        if (!!this.tab && this.tab.status === TabStatuses.OPEN) {
            await this.activateOrder();
        } else {
            await this.navigate(paymentType);
        }
    }

    async manualOrderItemCredit(orderItem: OrderItem, compDefinition: CompDefinition) {
        const modal = await this.modalController.create({
            component: CreateOrderItemCreditComponent,
            componentProps: {
                orderItem,
                compDefinition
            }
        });

        await modal.present();
        await modal.onDidDismiss().then(dataReturned => {
            if (dataReturned != null && dataReturned.data != null) {
                const credit = new OrderItemCredit(dataReturned.data);
                const updated = clone<OrderItem>(orderItem);
                if (credit.amount > 0) {
                    if (!orderItem.credits) {
                        updated.credits = [];
                    }
                    updated.tokenPayment = null;
                    updated.membershipTokenGroupId = null;
                    updated.credits.push(credit);
                }

                this.removeOrderItems([orderItem]);
                this.addOrderItems([updated]);
            }
        });
    }

    async addCredit(orderItem: OrderItem) {
        const modal = await this.modalController.create({
            component: SelectCompComponent,
            componentProps: {
                availableComps: this.availableOrderComps
                    .filter(c => !c.items || c.items.length === 0 || c.items.some(p => p.priceId === orderItem.selectedPrice.id))
            },
            cssClass: this.availableOrderComps.length > 4 ? 'menu-modal' : ''
        });

        await modal.present();

        await modal.onDidDismiss().then(async dataReturned => {
            if (dataReturned != null && dataReturned.data != null) {
                const compDefinition = new CompDefinition(dataReturned.data);

                if (compDefinition.type === CompDefinitionTypes.MANUAL) {
                    await this.manualOrderItemCredit(orderItem, compDefinition);
                } else {
                    const updated = new OrderItem(orderItem);
                    const credit = new OrderItemCredit({compDefinition});

                    credit.addIndex = this.orderItemCreditIndex++;

                    if (compDefinition.type === CompDefinitionTypes.DEFINED) {
                        credit.amount = compDefinition.amount;
                    } else if (compDefinition.type === CompDefinitionTypes.PERCENT) {
                        credit.amount = multiplyCurrency(orderItem.calcTotal(), divideCurrency(compDefinition.amount, 100));
                    } else if (compDefinition.type === CompDefinitionTypes.COMBINED_PRICE && compDefinition.combinedCount === 1) {
                        credit.amount = subtractCurrency(orderItem.calcTotal(), compDefinition.amount);
                    }

                    const remaining = subtractCurrency(updated.calcTotal(), updated.calcCredits());

                    if (remaining < credit.amount) {
                        credit.amount = remaining;
                    }

                    if (credit.amount > 0) {
                        if (!updated.credits) {
                            updated.credits = [];
                        }
                        updated.credits.push(credit);
                        updated.tokenPayment = null;
                        updated.membershipTokenGroupId = null;
                    }

                    this.removeOrderItems([orderItem]);
                    this.addOrderItems([updated]);
                }
            }
        });
    }

    async removeTabCredit(tabCredit: TabCredit) {
        const alert = await this.alertController.create({
            cssClass: 'brewbill-alert',
            header: 'Remove Comp',
            message: `Would you like to remove ${tabCredit.compDefinition.name}?`,
            buttons: [
                {
                    text: 'No'
                }, {
                    text: 'Yes',
                    handler: () => {
                        if (tabCredit.autoApplied) {
                            this.tab.preventAutoCredit = true;
                        }

                        this.tab.credits = this.tab.credits.filter(c => c.compDefinition.id !== tabCredit.compDefinition.id);
                        this.tab = this.tabCalculationService.recalculate(this.tab);
                    }
                }
            ]
        });

        await alert.present();
    }

    async removeCredit(data: any) {
        const orderItem: OrderItem = data.orderItem;
        const updated = clone<OrderItem>(orderItem);
        const credit: OrderItemCredit = data.credit;
        updated.preventAutoCredit = true;

        if (!!updated.credits && updated.credits.length > 0) {
            updated.credits = updated.credits.filter(c => credit.addIndex !== c.addIndex);
            this.removeOrderItems([orderItem]);
            this.addOrderItems([updated]);
        }
        this.applyAll(this.order);
    }

    async removeTokens(data: any) {
        const alert = await this.alertController.create({
            cssClass: 'brewbill-alert',
            header: 'Confirm Token Removal',
            message: `Are you sure you want to remove this token payment?`,
            buttons: [
                {
                    text: 'No',
                    role: 'cancel'
                }, {
                    text: 'Yes',
                    handler: () => {
                        const orderItem: OrderItem = data;
                        const updated = clone<OrderItem>(orderItem);

                        if (!!updated.tokenPayment) {
                            updated.tokenPaymentRefunded = updated.tokenPayment;
                            updated.tokenPayment = null;
                            updated.membershipTokenGroupId = null;
                            updated.preventAutoCredit = true;
                            this.removeOrderItems([orderItem]);
                            this.addOrderItems([updated]);
                        }
                    }
                }
            ]
        });

        await alert.present();
    }

    addTokens(data: OrderItem) {
        const orderItem = this.order.items.find(i => i.key === data.key);
        const updated = clone<OrderItem>(orderItem);
        if (!!orderItem && !orderItem.tokenPayment) {
            updated.tokenPaymentRefunded = data.tokenPaymentRefunded;
            updated.tokenPayment = data.tokenPayment;
            updated.membershipTokenGroupId = data.membershipTokenGroupId;
            updated.preventAutoCredit = data.preventAutoCredit;
            this.removeOrderItems([orderItem]);
            this.addOrderItems([updated]);
        }
    }

    removeMember() {
        this.membershipService.setCurrent(null);
    }

    notesChanged(noteDefinitions: TabNoteDefinition[]) {
        this.applyNotes = noteDefinitions;
        if (!!this.order) {
            if (!this.tab) {
                this.tab = this.offlineTabService.initialize(this.order, this.applyNotes);
            } else {
                this.offlineTabService.applyNoteDefinitions(this.tab, this.applyNotes);
            }
            this.applyAll(this.order);
        }
    }

    async selectMember() {
        const modal = await this.modalController.create({
            component: SelectMemberComponent,
            componentProps: {
                currentMember: this.activeMembership
            },
            cssClass: 'menu-modal'
        });

        await modal.present();

        await modal.onDidDismiss().then(async dataReturned => {
            const membership = !!dataReturned && !!dataReturned.data && dataReturned.data !== 'NONE'
                ? new Membership(dataReturned.data)
                : null;

            if (!!membership && membership.id > 0) {
                if (!!membership && !!this.openTabs && this.openTabs.length > 0) {
                    const memberTabs = this.openTabs.filter(t => !!t.person && t.person.id === membership.person.id);
                    if (memberTabs.length === 1) {
                        this.tabService.setCurrent(new Tab(memberTabs[0]));
                    }
                }
                this.membershipService.setCurrent(membership);
            } else if (!!dataReturned && !!dataReturned.data && dataReturned.data === 'NONE') {
                this.membershipService.setCurrent(null);
            }
        });
    }

    async renewMembership(orderItem: OrderItem) {
        const modal = await this.modalController.create({
            component: RenewMembershipComponent,
            componentProps: {
                organization: this.organization
            },
            cssClass: 'present-card-modal'
        });

        await modal.present();

        modal.onDidDismiss().then(dataReturned => {
            if (!!dataReturned && !!dataReturned.data) {
                this.addMembershipRenewal(orderItem, dataReturned.data);
            }
        });
    }

    async addMembershipRenewal(orderItem: OrderItem, membershipData: MembershipRenewal) {
        let orderItemIndex = !!orderItem ? orderItem.addIndex : null;
        if (!orderItemIndex) {
            orderItemIndex = (!!this.order && !!this.order.items && this.order.items.length > 0) ?
                this.order.items.reduce((max, item) => max = (max > item.addIndex) ? max : item.addIndex + 1, 0)
                : 1;
        }

        const updated = this.orderItemService.initializeOrderItem(null, null);
        updated.membershipRenewal = new MembershipRenewal(membershipData);

        if (!!orderItem && !!orderItem.membershipRenewal && !!orderItem.membershipRenewal.person) {
            updated.membershipRenewal.person = orderItem.membershipRenewal.person;
            updated.membershipRenewal.membershipName = `${updated.membershipRenewal.person.firstName} ${updated.membershipRenewal.person.lastName}`;
        }

        if (!!this.activeMembership) {
            updated.membershipRenewal.membershipId = this.activeMembership.id;
            updated.membershipRenewal.membershipName = !!this.activeMembership.person
                ? this.activeMembership.person.name : 'No Person Information';
        }
        updated.membershipRenewal.quantity = !!orderItem && !!orderItem.membershipRenewal.quantity
            ? orderItem.membershipRenewal.quantity : 1;
        updated.total = multiplyCurrency(updated.membershipRenewal.membershipProgram.amount, updated.membershipRenewal.quantity);

        updated.menuItem = null;
        updated.selectedPrice = null;
        updated.addIndex = orderItemIndex;

        if (!this.organization.organizationPreference.monthlyMembershipSubscription) {
            updated.membershipHostFee = updated.membershipRenewal.membershipProgram.lifetime
                ? 24
                : updated.membershipRenewal.months;
            updated.membershipOrganizationFee =
                updated.membershipRenewal.membershipProgram.feeStructure === MembershipProgramFeeStructure.DUAL
                    ? updated.membershipHostFee : 0;
        } else {
            updated.membershipHostFee = 0;
            updated.membershipOrganizationFee = 0;
        }

        if (updated.membershipRenewal.membershipProgram.feeStructure === MembershipProgramFeeStructure.ABSORBED) {
            // for now just apply $1/month make this configurable in the future
            updated.total = subtractCurrency(updated.total, updated.membershipHostFee);
            updated.membershipFeeIncluded = true;
        }

        if (!this.activeMembership) {
            await this.editMemberPersonInfo(updated.membershipRenewal);
        }

        if (!!this.activeMembership && !!this.order && !!this.order.items) {
            const foundItems = this.order.items.filter(i => !!i.membershipRenewal);
            if (!!foundItems && foundItems.length > 0) {
                this.removeOrderItems(foundItems);
            }
        } else if (!!orderItem) {
            this.removeOrderItems([orderItem]);
        }
        this.addOrderItems([updated]);
    }

    async editMemberPersonInfo(membershipRenewal: MembershipRenewal) {
        const person = !!membershipRenewal.person ? membershipRenewal.person : (!!this.tab && !!this.tab.person
            ? this.tab.person
            : new Person());
        person.id = null;

        const modal = await this.modalController.create({
            component: EditMembershipComponent,
            componentProps: {
                title: `Add Contact for ${membershipRenewal.membershipProgram.name} ${this.underscoreConversionPipe.transform(membershipRenewal.getDescription())} ${this.organization.membershipLabel}`,
                member: new Membership({
                    id: membershipRenewal.membershipId,
                    person
                }),
                skipSave: true,
                personUpdate: true
            }
        });

        await modal.present();

        const dataReturned = await modal.onDidDismiss();

        if (dataReturned !== null && dataReturned.data != null) {
            membershipRenewal.person = new Person(dataReturned.data.person);
            membershipRenewal.membershipName = `${membershipRenewal.person.firstName} ${membershipRenewal.person.lastName}`;
        }
    }
}
